'use client'

import * as React from 'react'
import { Section } from '../layout/Section'
import { Container } from '../layout/Container'
import { FadeIn } from '../motion/FadeIn'

interface APIEndpoint {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  path: string
  description?: string
  parameters?: {
    name: string
    type: string
    description: string
    required?: boolean
  }[]
  example?: string
}

interface RateLimit {
  plan: string
  requestsPerMinute: string | number
  requestsPerDay: string | number
}

interface DocsPageProps {
  heading?: string
  subheading?: string
  icon?: React.ReactNode
  authentication?: {
    description: string
    headerExample: string
  }
  apiKeyLink?: string
  endpoints?: APIEndpoint[]
  rateLimits?: RateLimit[]
}

export function DocsPage({
  heading = 'API Documentation',
  subheading,
  icon,
  authentication,
  apiKeyLink = '/dashboard/api-keys',
  endpoints = [],
  rateLimits = [],
}: DocsPageProps) {
  const methodColors: Record<string, string> = {
    GET: 'text-green-400',
    POST: 'text-blue-400',
    PUT: 'text-yellow-400',
    PATCH: 'text-orange-400',
    DELETE: 'text-red-400',
  }

  return (
    <Section className="min-h-[60vh]">
      <Container size="md">
        <div className="flex items-center gap-3 mb-6">
          {icon && (
            <FadeIn>
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-[var(--accent)]/10">
                {icon}
              </div>
            </FadeIn>
          )}
          <FadeIn delay={0.1}>
            <h1 className="headline-lg">{heading}</h1>
          </FadeIn>
        </div>

        {subheading && (
          <FadeIn delay={0.2}>
            <p className="body-lg mb-12">{subheading}</p>
          </FadeIn>
        )}

        {authentication && (
          <FadeIn delay={0.3}>
            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-[var(--landing-text-primary)] mb-4">
                Authentication
              </h2>
              <p className="text-[var(--landing-text-secondary)] mb-4">
                {authentication.description}{' '}
                <a href={apiKeyLink} className="text-[var(--accent)] hover:underline">
                  dashboard
                </a>.
              </p>
              <div className="bg-[var(--landing-bg-muted)] rounded-lg p-4 font-mono text-sm">
                <span className="text-[var(--landing-text-tertiary)]">Header:</span>{' '}
                <span className="text-[var(--accent)]">{authentication.headerExample}</span>
              </div>
            </section>
          </FadeIn>
        )}

        {endpoints.map((endpoint, index) => (
          <FadeIn key={`${endpoint.method}-${endpoint.path}`} delay={0.3 + index * 0.1}>
            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-[var(--landing-text-primary)] mb-4">
                {endpoint.description || endpoint.path}
              </h2>
              <div className="bg-[var(--landing-bg-muted)] rounded-lg p-4 font-mono text-sm mb-4">
                <span className={methodColors[endpoint.method]}>{endpoint.method}</span>{' '}
                <span className="text-[var(--landing-text-primary)]">{endpoint.path}</span>
              </div>

              {endpoint.parameters && endpoint.parameters.length > 0 && (
                <>
                  <h3 className="text-lg font-medium text-[var(--landing-text-primary)] mb-2">
                    Parameters
                  </h3>
                  <div className="bg-[var(--landing-bg-muted)] rounded-lg overflow-hidden mb-4">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-[var(--landing-border)]">
                          <th className="text-left p-3 text-[var(--landing-text-tertiary)]">Parameter</th>
                          <th className="text-left p-3 text-[var(--landing-text-tertiary)]">Type</th>
                          <th className="text-left p-3 text-[var(--landing-text-tertiary)]">Description</th>
                        </tr>
                      </thead>
                      <tbody className="text-[var(--landing-text-secondary)]">
                        {endpoint.parameters.map((param, pIndex) => (
                          <tr
                            key={param.name}
                            className={pIndex < endpoint.parameters!.length - 1 ? 'border-b border-[var(--landing-border)]' : ''}
                          >
                            <td className="p-3 font-mono text-[var(--accent)]">{param.name}</td>
                            <td className="p-3">{param.type}</td>
                            <td className="p-3">
                              {param.description}
                              {param.required && ' (required)'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {endpoint.example && (
                <>
                  <h3 className="text-lg font-medium text-[var(--landing-text-primary)] mb-2">
                    Example Request
                  </h3>
                  <div className="bg-[var(--landing-bg-muted)] rounded-lg p-4 font-mono text-sm overflow-x-auto">
                    <pre className="text-[var(--landing-text-secondary)] whitespace-pre-wrap">
                      {endpoint.example}
                    </pre>
                  </div>
                </>
              )}
            </section>
          </FadeIn>
        ))}

        {rateLimits.length > 0 && (
          <FadeIn delay={0.5}>
            <section>
              <h2 className="text-2xl font-semibold text-[var(--landing-text-primary)] mb-4">
                Rate Limits
              </h2>
              <div className="bg-[var(--landing-bg-muted)] rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[var(--landing-border)]">
                      <th className="text-left p-3 text-[var(--landing-text-tertiary)]">Plan</th>
                      <th className="text-left p-3 text-[var(--landing-text-tertiary)]">Requests/min</th>
                      <th className="text-left p-3 text-[var(--landing-text-tertiary)]">Requests/day</th>
                    </tr>
                  </thead>
                  <tbody className="text-[var(--landing-text-secondary)]">
                    {rateLimits.map((limit, index) => (
                      <tr
                        key={limit.plan}
                        className={index < rateLimits.length - 1 ? 'border-b border-[var(--landing-border)]' : ''}
                      >
                        <td className="p-3">{limit.plan}</td>
                        <td className="p-3">{limit.requestsPerMinute}</td>
                        <td className="p-3">{limit.requestsPerDay}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </FadeIn>
        )}
      </Container>
    </Section>
  )
}
